import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Briefcase, Users, AlertTriangle } from 'lucide-react';
import JobPicker from '../jobs/JobPicker';

const MAX_BULK = 50;

const BulkShortlistDialog = ({
  isOpen,
  onClose,
  selectedCount,
  jobs = [],
  jobId,
  setJobId,
  remarks,
  setRemarks,
  loading,
  onSubmit,
}) => {
  const tooMany = selectedCount > MAX_BULK;
  const noJobs = jobs.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[85vh] flex flex-col"
        data-testid="bulk-shortlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-teal-600" />
            Add to Pipeline
          </DialogTitle>
          <DialogDescription>
            Shortlist <strong data-testid="bulk-shortlist-count">{selectedCount}</strong>{' '}
            candidate{selectedCount === 1 ? '' : 's'} into a job's <strong>Sourced</strong>{' '}
            stage. They'll appear immediately in your Command Center.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
          {/* Selection summary banner */}
          <div className="flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50 p-3">
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700">
              <Users className="w-4 h-4" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-slate-900">
                {selectedCount} candidate{selectedCount === 1 ? '' : 's'} selected
              </p>
              <p className="text-xs text-slate-500">
                Already-applied candidates will be safely skipped, not duplicated.
              </p>
            </div>
          </div>

          {tooMany ? (
            <div
              className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="bulk-shortlist-too-many"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <div>
                You've selected <strong>{selectedCount}</strong> candidates, but bulk shortlist supports up to <strong>{MAX_BULK}</strong> at once. Please reduce your selection.
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Briefcase className="w-4 h-4" />
              Choose a job *
            </Label>
            <JobPicker
              jobs={jobs}
              value={jobId || ''}
              onChange={setJobId}
              placeholder="Pick a job…"
              testid="bulk-shortlist-job"
            />
            {noJobs ? (
              <p className="text-xs text-slate-500">
                You don't have any open jobs yet. Create a job first from the Jobs page.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Remarks (Optional)</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g., Hand-picked from Python pool — strong fit on backend"
              rows={2}
              data-testid="bulk-shortlist-remarks-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            data-testid="bulk-shortlist-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={loading || !jobId || tooMany || noJobs}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="bulk-shortlist-submit-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Shortlist {selectedCount} to Pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkShortlistDialog;
